<script lang="ts">
  import * as v from "valibot";
  import AuthLayout from "../auth/AuthLayout.svelte";
  import Field from "$lib/components/Field.svelte";
  import Button from "$lib/components/Button.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import { redeemClaim } from "$lib/api/endpoints";
  import { newIdempotencyKey } from "$lib/api/idempotency";
  import { AppError } from "$lib/api/problem";
  import { session } from "$lib/stores/session.svelte";
  import { router } from "../../router.svelte";
  import { createForm } from "$lib/forms/form.svelte";

  // One-time enterprise claim. Redemption requires an authenticated, verified-email
  // session (G-13) and never changes the current session. The token is scrubbed from
  // the URL immediately; because the passwordless email round-trip leaves this page,
  // it is retained ONLY in sessionStorage (same-tab, cleared on redemption/failure)
  // so the user lands back here still holding their claim. It is never rendered,
  // logged, or attached to telemetry.
  const STORAGE_KEY = "epidev.claim.token";

  function loadStoredToken(): string | null {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }
  function storeToken(token: string): void {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, token);
    } catch {
      /* storage unavailable — the in-memory copy still covers the authed path */
    }
  }
  function clearStoredToken(): void {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clear */
    }
  }

  const queryToken = router.match.query.get("token");
  router.scrubQuery(["token"]);
  if (queryToken) storeToken(queryToken);
  let claimToken = $state<string | null>(queryToken ?? loadStoredToken());

  const redeemKey = newIdempotencyKey();
  const mode = $derived(session.authenticated ? "org" : "auth");

  const CLAIM_MESSAGES: Record<string, string> = {
    claim_required: "This claim link is invalid.",
    invalid_claim: "This claim link is invalid.",
    claim_expired: "This claim link has expired. Ask your EpiDev contact for a new one.",
    claim_redeemed: "This claim link can be used once and has now been used.",
    claim_identity_mismatch: "This claim is bound to a different account.",
    customer_inactive: "This enterprise account is not active. Contact EpiDev.",
    entitlement_missing: "No entitlement is attached to this claim yet.",
    customer_already_bound: "This entitlement is already bound to an organization."
  };
  // Terminal claim errors: the token is spent or unusable, so stop retaining it.
  const TERMINAL_CODES = new Set(["invalid_claim", "claim_expired", "claim_redeemed", "customer_already_bound"]);

  function claimMessage(error: AppError): string {
    return CLAIM_MESSAGES[error.code] ?? error.userMessage;
  }

  function continueToSignIn(): void {
    router.navigate(`/login?returnTo=${encodeURIComponent("/claim")}`);
  }

  const orgForm = createForm(
    v.object({ organizationName: v.pipe(v.string(), v.trim(), v.minLength(2, "Name your organization.")) }),
    { organizationName: "" },
    async (values) => {
      if (!claimToken) throw new AppError({ title: "Invalid claim", status: 400, detail: CLAIM_MESSAGES.invalid_claim!, code: "invalid_claim", correlationId: "n/a" });
      try {
        const result = await redeemClaim(
          { claimToken, organizationName: values.organizationName.trim() },
          redeemKey
        );
        clearStoredToken();
        // The claim doesn't establish/alter the session — refresh to pick up the new org.
        await session.refresh();
        router.navigate(`/orgs/${result.orgId}/overview`, { replace: true });
      } catch (err) {
        if (err instanceof AppError && TERMINAL_CODES.has(err.code)) clearStoredToken();
        throw err;
      }
    }
  );
</script>

<AuthLayout
  title="Claim your enterprise entitlement"
  subtitle="Bind your EpiDev enterprise plan to a new organization."
>
  {#if !claimToken}
    <Banner tone="error" title="Invalid claim link">
      This claim link is missing or has already been used.
    </Banner>
    <Button href="/login" fullWidth>Back to sign in</Button>
  {:else if mode === "auth"}
    <Banner tone="info" title="Verify your email first">
      Enterprise claims are redeemed by a signed-in account with a verified email. We'll
      email you a secure sign-in link and bring you straight back to this claim — no
      password needed, and new accounts are created automatically.
    </Banner>
    <Button fullWidth onclick={continueToSignIn}>Continue with email verification</Button>
  {:else}
    <Banner tone="info" title="One-time claim">
      This claim link can be used once. Name your new organization to bind the entitlement.
    </Banner>
    {#if orgForm.formError}
      <Banner tone="error" title="We couldn't redeem the claim" correlationId={orgForm.formError.correlationId}>
        {claimMessage(orgForm.formError)}
      </Banner>
    {/if}
    <form onsubmit={(e) => { e.preventDefault(); void orgForm.submit(); }} class="stack">
      <Field id="cl-org" label="Organization name" bind:value={orgForm.values.organizationName} error={orgForm.touched.organizationName ? orgForm.errors.organizationName : undefined} onblur={() => orgForm.touch("organizationName")} required />
      <Button type="submit" fullWidth loading={orgForm.busy}>Create organization</Button>
    </form>
  {/if}
</AuthLayout>
